import { FormConfig, Text } from '@lbcamden/lib-react';
import { z } from 'zod';

import {
  escapeRiskFactorOptions,
  fireRiskFactorOptions,
  reactionRiskFactorOptions,
} from '~/common/config/pcfra/fireSafetySurvey';
import { fireSafetySurveySchema } from '~/common/schemas/pcfra';

import { CheckboxOption } from '../../form/ConditionalFormInput';
import {
  getConditionalCheckboxGroupRenderFn,
  getConditionalTextAreaRenderFn,
  numberedLabel,
} from '../../form/helpers';
import { ClutterScale } from '../ClutterScale';

export type FireSafetySurveyFormValues = z.infer<typeof fireSafetySurveySchema>;

const yesLabel = 'Yes - Mark all risk factors';
const noLabel = 'No - Skip to next question';

const otherOption = (sectionName: string): CheckboxOption => ({
  id: `${sectionName}-other`,
  label: 'Other (please specify)',
  isCustom: true,
});

const styledEscapeRiskFactorOptions: CheckboxOption[] = escapeRiskFactorOptions.map((opt) =>
  [
    'mobilityIssues',
    'visuallyImpaired',
    'understandingIssues',
    'hasClutter',
    'bedOrChairbound',
  ].includes(opt.id)
    ? {
        ...opt,
        label: (
          <Text $color="accent" $weight={600}>
            {opt.label}
          </Text>
        ),
      }
    : opt,
);

export const fireSafetySurveyFormConfig = (
  values: Partial<FireSafetySurveyFormValues>,
): FormConfig<FireSafetySurveyFormValues> => ({
  hideAllRequiredIndicators: true,
  fieldConfigs: {
    fireRiskFactors: {
      label: numberedLabel(1, 'Does the individual have an increased fire risk?'),
      customRenderFn: getConditionalCheckboxGroupRenderFn({
        yesLabel,
        noLabel,
        options: [...fireRiskFactorOptions, otherOption('fireRiskFactors')],
      }),
    },
    reactionRiskFactors: {
      readOnly: values.fireRiskFactors === undefined,
      label: numberedLabel(2, 'Would the individual be less able to react to an alarm or fire?'),
      customRenderFn: getConditionalCheckboxGroupRenderFn({
        yesLabel,
        noLabel,
        options: [...reactionRiskFactorOptions, otherOption('reactionRiskFactors')],
      }),
    },
    escapeRiskFactors: {
      readOnly: values.reactionRiskFactors === undefined,
      label: numberedLabel(3, 'Does the individual have a reduced ability to escape?'),
      customRenderFn: getConditionalCheckboxGroupRenderFn({
        yesLabel,
        noLabel,
        nestedContent: <ClutterScale buttonLabel="View clutter scale" />,
        options: [...styledEscapeRiskFactorOptions, otherOption('escapeRiskFactors')],
      }),
    },
    smokeOrHeatAlarms: {
      readOnly: values.escapeRiskFactors === undefined,
      label: numberedLabel(
        4,
        'Are there any smoke or heat alarms fitted within the individual’s home?',
      ),
      customRenderFn: getConditionalTextAreaRenderFn({
        textAreaLabel: 'Specify which rooms have them fitted:',
      }),
    },
    carbonMonoxideAlarm: {
      readOnly: values.smokeOrHeatAlarms === undefined,
      label: numberedLabel(
        5,
        'Has a carbon monoxide alarm been fitted where gas or solid fuels are used?',
      ),
      customRenderFn: getConditionalTextAreaRenderFn({
        textAreaLabel: 'Specify which rooms have them fitted:',
      }),
    },
    comments: {
      readOnly: values.carbonMonoxideAlarm === undefined,
      label: numberedLabel(6, 'Additional comments (optional)'),
      type: 'textarea',
    },
  },
});
